/**
 * Turns a track into notes, ahead of time.
 *
 * Like the core simulation, this module takes time as an argument rather than
 * reading a clock: `step(dtMs)` advances a musical position and returns every
 * note that falls inside the next `LOOKAHEAD_MS`, each carrying an offset in
 * seconds from now. A renderer schedules them against its own audio clock, so
 * the timing stays sample-accurate even though the tick is an animation frame.
 *
 * Position is counted in sixteenths, not milliseconds. That is what makes a
 * tempo change safe mid-bar: the cursor keeps its place in the bar and only the
 * width of a step changes underneath it. The cursor never moves backwards,
 * which is the whole of the duplicate-suppression story.
 */

import {
  BARS,
  STEPS_PER_BAR,
  chordTone,
  noteHz,
  type LayerId,
  type LayerSpec,
  type NoteSpec,
  type TrackSpec,
} from './score.ts';

/** How far ahead notes are handed to the renderer. */
export const LOOKAHEAD_MS = 150;

/**
 * Ceiling on one call's delta. A backgrounded tab coming back with a thirty
 * second gap advances one second of music, not thirty seconds of it.
 */
const MAX_STEP_MS = 1000;

export interface ScheduledNote extends NoteSpec {
  /** Seconds from now. Never negative. */
  when: number;
  layer: LayerId;
  /** Absolute sixteenth index since the last reset. Unique per layer. */
  step: number;
}

export class Sequencer {
  private current: TrackSpec;
  private bpm: number;
  private layers: ReadonlySet<LayerId> = new Set();
  private readonly intensity = new Map<LayerId, number>();

  /** Musical position in sixteenths, fractional. */
  private position = 0;
  /** Next sixteenth to emit. Only ever moves forward. */
  private cursor = 0;

  constructor(track: TrackSpec) {
    this.current = track;
    this.bpm = track.baseBpm;
  }

  get track(): TrackSpec {
    return this.current;
  }

  /**
   * Swap the arrangement without losing the place in the bar, so a screen
   * change does not restart the pulse.
   */
  setTrack(track: TrackSpec): void {
    this.current = track;
  }

  setBpm(bpm: number): void {
    this.bpm = Math.max(1, bpm);
  }

  setLayers(layers: ReadonlySet<LayerId>): void {
    this.layers = layers;
  }

  /** Multiplier on a layer's gain and filter frequency. 1 is the written value. */
  setLayerIntensity(layer: LayerId, value: number): void {
    this.intensity.set(layer, Math.max(0, value));
  }

  /** Advance, and hand back everything due inside the lookahead window. */
  step(dtMs: number): ScheduledNote[] {
    const stepMs = this.stepDurationMs();
    this.position += Math.min(Math.max(dtMs, 0), MAX_STEP_MS) / stepMs;

    const horizon = this.position + LOOKAHEAD_MS / stepMs;
    const notes: ScheduledNote[] = [];

    // The cursor routinely sits behind the position when a frame is longer
    // than the lookahead; those steps are due now, not stale, so `when` floors
    // at zero and they play immediately. `MAX_STEP_MS` bounds the pile-up.
    while (this.cursor <= horizon) {
      const when = Math.max(0, (this.cursor - this.position) * stepMs) / 1000;
      for (const layer of this.current.layers) {
        if (!this.layers.has(layer.id)) continue;
        const note = this.noteAt(layer, this.cursor, when);
        if (note) notes.push(note);
      }
      this.cursor++;
    }

    return notes;
  }

  /** Return to the top of the phrase. */
  reset(): void {
    this.position = 0;
    this.cursor = 0;
  }

  private stepDurationMs(): number {
    return 60000 / this.bpm / 4;
  }

  private noteAt(layer: LayerSpec, absolute: number, when: number): ScheduledNote | null {
    const bar = Math.floor(absolute / STEPS_PER_BAR);
    if (layer.bars && !layer.bars.includes(bar % BARS)) return null;

    const index = layer.steps.indexOf(absolute % STEPS_PER_BAR);
    if (index < 0) return null;

    const tone = layer.tones[index % layer.tones.length];
    if (tone === null || tone === undefined) return null;

    const scale = this.intensity.get(layer.id) ?? 1;
    const voice = layer.voice;
    const note: ScheduledNote = {
      ...voice,
      freq: noteHz(chordTone(bar, tone), layer.octave),
      gain: voice.gain * scale,
      layer: layer.id,
      step: absolute,
      when,
    };
    if (voice.filter) note.filter = { ...voice.filter, freq: voice.filter.freq * scale };
    return note;
  }
}
